import React, { useEffect, useState } from 'react';
import { Drawer, Upload, Input, Button, Space, Typography, Modal, message, Alert } from 'antd';
import { PlusOutlined, PictureOutlined } from '@ant-design/icons';
import { uiStatusOf, currentVersion, uiStatusLabel, designImage } from './uiDesignMock';

const { Text } = Typography;
const { TextArea } = Input;

/**
 * UI 设计上传 / 替换抽屉（管理端）
 * - 多张图片（picture-card）：可预览、可删除
 * - 文字说明：非必填
 * - 客户已确认状态下替换需二次确认，替换后回到「待客户确认」
 */
const UIDesignUploadDrawer = ({ open, record, state, onClose, onSubmit }) => {
    const [fileList, setFileList] = useState([]);
    const [note, setNote] = useState('');

    const status = uiStatusOf(state);
    const last = currentVersion(state);

    useEffect(() => {
        if (open) {
            setFileList([]);
            setNote('');
        }
    }, [open, record]);

    const handleChange = ({ fileList: fl }) => {
        setFileList(fl.map((f) => ({
            ...f,
            status: 'done',
            url: f.url || (f.originFileObj ? URL.createObjectURL(f.originFileObj) : undefined) || f.thumbUrl,
        })));
    };

    // 无真实文件时，可插入示例设计稿便于演示
    const addSample = () => {
        const names = ['登录页', '首页', '设备列表', '个人中心'];
        setFileList((prev) => [
            ...prev,
            ...names.map((n, i) => ({
                uid: `sample-${Date.now()}-${i}`,
                name: `${n}.png`,
                status: 'done',
                url: designImage(n, `v${(last?.version || 0) + 1}`, '#1677ff'),
            })),
        ]);
    };

    const submit = () => {
        if (!fileList.length) { message.warning('请先上传设计稿'); return; }
        const screens = fileList.map((f) => ({ name: f.name, url: f.url || f.thumbUrl }));
        const doSubmit = () => {
            onSubmit(screens, note);
            onClose();
            message.success('UI 设计已提交，等待确认');
        };
        if (status === 'confirmed') {
            Modal.confirm({
                title: '替换 UI 设计',
                content: '替换后需客户重新确认，确认替换？',
                okText: '确认替换',
                cancelText: '取消',
                onOk: doSubmit,
            });
            return;
        }
        doSubmit();
    };

    return (
        <Drawer
            title={`${status === 'none' ? '上传 UI 设计' : '替换 UI 设计'} · ${record?.appName || ''}`}
            open={open}
            width={640}
            onClose={onClose}
            footer={(
                <Space style={{ float: 'right' }}>
                    <Button onClick={onClose}>取消</Button>
                    <Button type="primary" onClick={submit}>提交，等待确认</Button>
                </Space>
            )}
        >
            {status === 'rejected' && last?.rejectReason && (
                <Alert type="error" showIcon style={{ marginBottom: 16 }}
                    message="客户已驳回，请按意见修改后重新上传"
                    description={last.rejectReason} />
            )}
            {status === 'confirmed' && (
                <Alert type="warning" showIcon style={{ marginBottom: 16 }}
                    message="客户已确认 UI 设计，替换后将回到「待客户确认」，需客户重新确认后才能构建一阶段。" />
            )}

            <Text strong style={{ display: 'block', marginBottom: 8 }}>设计稿（多张，可预览 / 删除）</Text>
            <Upload
                listType="picture-card"
                fileList={fileList}
                beforeUpload={() => false}
                onChange={handleChange}
                onRemove={(file) => { setFileList((prev) => prev.filter((f) => f.uid !== file.uid)); return true; }}
                accept="image/*"
                multiple
            >
                <div><PlusOutlined /><div style={{ marginTop: 6 }}>上传图片</div></div>
            </Upload>
            <Button type="link" size="small" icon={<PictureOutlined />} style={{ padding: 0, marginBottom: 16 }} onClick={addSample}>
                插入示例设计稿（演示用）
            </Button>

            <Text strong style={{ display: 'block', marginBottom: 8 }}>文字说明（选填）</Text>
            <TextArea rows={3} maxLength={200} showCount value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="整体设计说明 / 本次变更说明" />

            {last && (
                <div style={{ marginTop: 16 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        当前版本 v{last.version} · {uiStatusLabel(status, true)} · 提交于 {last.uploadedAt}（{last.uploadedBy}）
                    </Text>
                </div>
            )}
        </Drawer>
    );
};

export default UIDesignUploadDrawer;
